import * as dateFns from 'date-fns'

/**
 * Used to capture a {@link Date} with a particular timezone
 */
export interface DateWithUtcOffset {
  date: Date
  /**
   * Equivalent to {@link Date.getTimezoneOffset} in the relevant timezone
   */
  utcOffset?: number
}
export namespace DateWithUtcOffset {
  /**
   * Represents the given {@link DateWithUtcOffset} as a {@link Date} which will
   * behave like the specified date in the given timezone, but only when using
   * locale-specific methods
   *
   * @example
   * const emulatedDate = emulateInLocalTimezone({
   *   // Assume EDT is the local timezone
   *   date: new Date('Wed Sep 17 2025 13:17:20 GMT-0400 (Eastern Daylight Time)'),
   *   utcOffset: -120 // CEDT is the desired timezone
   * })
   * // Correct (locale-specific)
   * console.log(emulatedDate.toLocaleString()) // '9/17/2025, 7:17:20 PM'
   * console.log(emulatedDate.getHours()) // 19
   * // Incorrect (not locale-specific)
   * console.log(emulatedDate.toISOString()) // '2025-09-17T23:17:20.000Z'
   * console.log(emulatedDate.getTime()) // 1758129440000
   */
  export function emulateInLocalTimezone({
    date,
    utcOffset,
  }: DateWithUtcOffset): Date {
    return utcOffset === undefined || utcOffset === null
      ? date
      : dateFns.addMinutes(date, date.getTimezoneOffset() - utcOffset)
  }

  /**
   * Formats the given {@link DateWithUtcOffset} according to the given format
   * string, in the desired timezone
   *
   * @see {@link https://date-fns.org/v4.1.0/docs/format} or {@link https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table}
   * @example
   * console.log(format({
   *   // Assume EDT is the local timezone
   *   date: new Date('Wed Sep 17 2025 13:17:20 GMT-0400 (Eastern Daylight Time)'),
   *   utcOffset: -120 // CEDT is the desired timezone
   * }, 'eee MMM d y H:mm')) // 'Wed Sep 17 2025 19:17'
   */
  export function format(
    dateWithUtcOffset: DateWithUtcOffset,
    formatStr: string,
  ): string {
    return dateFns.format(emulateInLocalTimezone(dateWithUtcOffset), formatStr)
  }
}
